import React, { createContext, useContext, useState } from "react";
import { PessoaResumoDTO } from "@/types/pessoa";
import { cn } from "@/lib/utils";
import { formatPersonName, PersonNameFormat } from "@/utils/name-format";
import { PersonAvatarView } from "./PersonAvatarView";
import { PersonProfileDialog } from "./profile/PersonProfileDialog";
import { PersonPreviewCard } from "./PersonPreviewCard";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { usePermissions } from "@/hooks/use-permissions";
import { usePessoaOnline } from "@/hooks/pessoas/use-pessoa-online";
import { AbrangenciaMap } from "@workflow/types";

interface PersonContextValue {
  pessoa: PessoaResumoDTO;

  isExColaborador: boolean;
  isAusente: boolean;
  isClickable: boolean;
}

const PersonContext = createContext<PersonContextValue | null>(null);

export function usePersonContext() {
  const context = useContext(PersonContext);
  if (!context) throw new Error("Subcomponentes devem ser usados dentro de <Person.Root>");
  return context;
}

interface PersonRootProps {
  pessoa: PessoaResumoDTO;
  children: React.ReactNode;
  className?: string;
  asClickable?: boolean;
}

export function PersonRoot({ pessoa, children, className, asClickable }: PersonRootProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!pessoa) return null;
  if (!pessoa.id) throw new Error("Pessoa sem ID");

  const isExColaborador = !pessoa.ativo;
  const isAusente = !!pessoa.ausente;

  const value = { pessoa, isExColaborador, isAusente, isClickable: !!asClickable };

  const content = (
    <div
      // Marca a div como interativa p/ escapar do overlay de link da linha no DataTable.
      data-interactive={asClickable ? "" : undefined}
      className={cn(
        "flex items-center gap-2",
        asClickable && "cursor-pointer transition-opacity hover:opacity-80",
        className,
      )}
      onClick={(event) => {
        if (asClickable) {
          event.stopPropagation();
          setIsDialogOpen(true);
        }
      }}
    >
      {children}
    </div>
  );

  return (
    <PersonContext.Provider value={value}>
      <HoverCard>
        <HoverCardTrigger render={content} />
        <HoverCardContent className="w-80">
          <PersonPreviewCard pessoa={pessoa} isClickable={!!asClickable} />
        </HoverCardContent>
      </HoverCard>

      {asClickable && isDialogOpen && (
        <PersonProfileDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          pessoaId={pessoa.id}
        />
      )}
    </PersonContext.Provider>
  );
}

// 3. AVATAR
interface PersonAvatarProps {
  className?: string;
  /** Classes extras das iniciais (ex.: `text-lg` num avatar grande). */
  fallbackClassName?: string;
}

export function PersonAvatar({ className, fallbackClassName }: PersonAvatarProps) {
  const { pessoa, isExColaborador, isAusente } = usePersonContext();
  const isOnline = usePessoaOnline(pessoa.ultimaInteracao);

  // Prioriza o thumb (mais leve) e cai para a foto original; iniciais como fallback.
  const fotoSrc = pessoa.fotoThumb || pessoa.foto;

  return (
    <PersonAvatarView
      nome={pessoa.nome}
      foto={fotoSrc}
      className={cn(isExColaborador && "opacity-50 grayscale", className)}
      fallbackClassName={cn("bg-muted font-bold text-muted-foreground", fallbackClassName)}
      overlay={
        // Indicador de férias visível mesmo quando só o avatar é renderizado (ex.: grupos de avatares).
        // Férias tem prioridade sobre a bolinha "online" — é o status mais específico dos dois.
        isAusente && !isExColaborador ? (
          <span
            className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full border border-background bg-amber-400 text-[7px] leading-none dark:bg-amber-500"
            title="Em férias"
            aria-label="Em férias"
          >
            🌴
          </span>
        ) : isOnline && !isExColaborador ? (
          <span
            className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5"
            title="Online"
            aria-label="Online"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full border border-background bg-emerald-500" />
          </span>
        ) : null
      }
    />
  );
}

// 4. NOME
interface PersonNameProps {
  prefix?: string;
  format?: PersonNameFormat;
  className?: string;
}

export function PersonName({ prefix, format = "full", className }: PersonNameProps) {
  const { pessoa, isExColaborador, isClickable } = usePersonContext();

  return (
    <span
      className={cn(
        "truncate font-medium leading-none",
        isExColaborador
          ? "text-muted-foreground line-through decoration-muted-foreground/50"
          : "text-foreground",
        isClickable &&
          "cursor-pointer transition-opacity hover:text-primary dark:hover:text-emerald-500",
        className,
      )}
      title={pessoa.nome} // Tooltip nativo útil caso corte com o truncate
    >
      {prefix && <span className="mr-1">{prefix}</span>}
      {formatPersonName(pessoa.nome, format)}
    </span>
  );
}

// 4. EMAIL
interface PersonEmailProps {
  className?: string;
}

export function PersonEmail({ className }: PersonEmailProps) {
  const { pessoa, isExColaborador } = usePersonContext();

  return (
    <span
      className={cn(
        "truncate font-medium leading-none",
        isExColaborador
          ? "text-muted-foreground line-through decoration-muted-foreground/50"
          : "text-foreground",
        className,
      )}
      title={pessoa.email}
    >
      {pessoa.email}
    </span>
  );
}

// 6. ABRANGÊNCIA (somente admin)
export function PersonAbrangencia() {
  const { pessoa } = usePersonContext();
  const { isAdmin } = usePermissions();

  // Visível apenas para admins; só renderiza quando o backend resolveu o nível.
  if (!isAdmin || pessoa.abrangenciaNivel === undefined) return null;

  return (
    <span
      className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-primary"
      title="Abrangência (visível apenas para admin)"
    >
      {AbrangenciaMap[pessoa.abrangenciaNivel]}
    </span>
  );
}

// 5. STATUS BADGES
export function PersonStatus() {
  const { isExColaborador, isAusente } = usePersonContext();

  if (!isExColaborador && !isAusente) return null;

  return (
    <div className="flex shrink-0 gap-1">
      {isExColaborador && (
        <span className="rounded-md bg-destructive/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-destructive">
          Ex-colab
        </span>
      )}

      {isAusente && (
        <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
          Férias
        </span>
      )}
    </div>
  );
}
